import React from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom-v5-compat'

import { noop } from '@/app/definitions'
import { CampaignTemplateType } from '@/app/module/campaigns/types'
import { useSaveUser } from '@/app/module/user/hooks/use-save-user'
import { selectUserDetails } from '@/app/module/user/store/selectors'
import { selectSkippingTours, selectUserInit } from '@/app/module/user/store/ts/selectors'
import { CommonUserType } from '@/app/module/user/types'

import { REPEAT_TOUR_KEY } from './definitions'
import Tour from './tour'
import { TourType } from './types'

type Props = {
  type: CampaignTemplateType
}

const TourContainer: React.FC<Props> = (props) => {
  const location = useLocation()
  const navigate = useNavigate()
  const initialized = useSelector(selectUserInit)
  const skippingTours = useSelector(selectSkippingTours) as Record<TourType, boolean>
  const userDetails = useSelector(selectUserDetails) as CommonUserType | ''
  const [save] = useSaveUser()

  const params = React.useMemo(() => new URLSearchParams(location.search), [location.search])
  const isRepeat = React.useRef(params.has(REPEAT_TOUR_KEY, 'true')).current

  const repeatQueryCleared = React.useRef(false)

  React.useEffect(() => {
    if (!isRepeat || repeatQueryCleared.current) {
      return
    }
    repeatQueryCleared.current = true
    navigate(location.pathname, { replace: true })
  }, [isRepeat, location.pathname, navigate])

  const skipTour = React.useCallback(
    (tourType: TourType) => {
      if (typeof userDetails === 'string') {
        return
      }

      save({
        uiStore: {
          ...userDetails.uiStore,
          skippingTours: {
            ...skippingTours,
            [tourType]: true,
          },
        },
      })
    },
    [skippingTours, userDetails, save],
  )

  if (!initialized && !isRepeat) {
    return null // don't show tours until user is loaded for the first time
  }

  return (
    <Tour
      {...props}
      skippingTours={!isRepeat ? skippingTours : ({} as Record<TourType, boolean>)}
      onTourEnd={skipTour}
      onTourStart={noop}
    />
  )
}

export default TourContainer
